/* -----------------Types--------------- */
import type { MixedItemDistResponse } from "@memorang/types";
import type { IconType } from "components/index";

import { isScreenshotMode } from "@constants/common";
import { mockedProgressHomeViewStats } from "@constants/mocked-data";
/* -----------------Helpers & Hooks--------------- */
import { groupTagsByType } from "@helpers/content/tag";
import { randomizeDistribution } from "@helpers/mocked-helpers";

import theme from "@configs/theme";
import { getParentMetadata } from "@helpers/content/metadata";
/* -----------------Child components--------------- */
import { getSectionTags } from "./tag";

const getDashboardData = (
	response: MixedItemDistResponse,
	handleNavigateToIAP: () => void,
	groupByCategory?: boolean | null,
	hasAccess?: boolean,
) => {
	const { contents, iconUrl, title, tags, freeTagIds } = response;
	const numItems = contents.reduce((a, c) => a + c.totalCount, 0);

	const metadata = getParentMetadata(contents, numItems);
	const { distribution } = metadata;
	const tagsByType = groupTagsByType(tags, "Display", freeTagIds, hasAccess);
	const sectionTags = getSectionTags(
		numItems,
		tagsByType,
		handleNavigateToIAP,
		hasAccess,
	);

	const { high, low, medium, none } = distribution;
	const masteryPercentage = Math.round(
		(high / (high + none + low + medium)) * 100,
	);

	const sections = Object.keys(tagsByType)
		.filter((type) => tagsByType[type].length > 0)
		.map((type) => {
			return {
				...sectionTags[0],
				title: type,
				tags: tagsByType[type].sort((a, b) => a.title.localeCompare(b.title)),
			};
		});

	const finalSectionTags = groupByCategory
		? sections
				.map((section) => {
					const { tags } = section;
					const newTags = tags.map((tag) => {
						const { distribution } = tag;

						return {
							...tag,
							distribution: isScreenshotMode
								? randomizeDistribution(distribution)
								: distribution,
						};
					});
					return {
						...section,
						tags: newTags,
					};
				})
				.sort((a, b) => {
					return a.title.localeCompare(b.title);
				})
		: sectionTags;
	return {
		distribution: isScreenshotMode
			? randomizeDistribution(distribution)
			: distribution,
		productTitle: title ?? "",
		productImage: iconUrl ?? "",
		sectionTags: finalSectionTags,
		masteryPercentage,
		numItems,
	};
};

const getStats = ({
	masteryPercentage,
	numAnswered,
	xp,
	isDarkMode,
}: {
	masteryPercentage: number;
	numAnswered: number;
	xp?: number;
	isDarkMode: boolean;
}) => {
	const stats = isScreenshotMode
		? mockedProgressHomeViewStats
		: [
				{
					label: "Mastered",
					icon: "brain" as IconType,
					value: `${masteryPercentage}%`,
					color: theme(isDarkMode).palette.purple.main,
				},
				{
					label: "Answered",
					icon: "check-decagram-outline" as IconType,
					value: numAnswered,
					color: theme().colors.secondary,
				},
				{
					label: "XP earned",
					icon: "medal-outline" as IconType,
					value: xp ? xp.toLocaleString() : 0,
					color: theme().colors.error,
				},
			];
	return stats;
};
export { getDashboardData, getStats };
